import React from "react";
import { createRoot } from "react-dom/client";
import { Provider } from "react-redux";
import { store } from "./app/store";
import App from "./App";
import reportWebVitals from "./reportWebVitals";
import "./index.css";
import CssBaseline from "@mui/material/CssBaseline";
import {
  GlobalStyles,
  Theme,
  ThemeProvider,
  createTheme,
  makeStyles,
  outlinedInputClasses,
} from "@mui/material";
import { BrowserRouter } from "react-router-dom";
import { Helmet } from "react-helmet";
import { GoogleReCaptcha, GoogleReCaptchaProvider } from "react-google-recaptcha-v3";
import { RECAPTCHA_SITE_KEY } from "./pages/landing_page/recaptcha";
import { recaptchaOnChanged } from "./pages/landing_page/LandingPageSlice";

declare module "@mui/material/styles" {
  interface TypographyVariants {
    helptext: React.CSSProperties;
    menulink: React.CSSProperties;
    error: React.CSSProperties;
  }

  // allow configuration using `createTheme`
  interface TypographyVariantsOptions {
    helptext?: React.CSSProperties;
    menulink?: React.CSSProperties;
    error?: React.CSSProperties;
  }
}

// Update the Typography's variant prop options
declare module "@mui/material/Typography" {
  interface TypographyPropsVariantOverrides {
    helptext: true;
    menulink: true;
    error: true;
  }
}
const labelFontSize = "20px";
const container = document.getElementById("root")!;
const root = createRoot(container);
const theme = createTheme({
  components: {
    MuiCssBaseline: {
      styleOverrides: `
        input {
          font-size: 20px;
          padding: 2px;
          margin: 5px;
          text-align: start;
          background-color: #ffffff;
          appearance: none;
          color: #41273B;
          border: none;
          width: 100%;
        }

        select {
          font-size: 20px;
          padding: 2px;
          margin: 5px;
          text-align: start;
          background-color: #ffffff;
          appearance: none;
          color: #41273B;
          border: none;
          -webkit-appearance: listbox !important;
          width: 100%;
        }


        a:visited {
          color: #41273B;
        }

        a:link {
          color: #ED0B5D;
        }

        a:hover {
          color: #41273B;
        }

        a:active { 
          color: #ED0B5D;
        }

        input::-webkit-outer-spin-button,
        input::-webkit-inner-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }

        /* Firefox */
        input[type=number] {
          -moz-appearance: textfield;
        }
      `,
    },

    MuiSelect: {
      defaultProps: {
        SelectDisplayProps: {
          style: {
            fontSize: "20px",
            color: "#41273B",
          },
        },
        inputProps: {
          style: {
            fontSize: "20px",
            color: "#41273B",
          },
        },
      },
      styleOverrides: {
        root: {
          "--TextField-brandBorderColor": "#41273B",
          "--TextField-brandBorderHoverColor": "#ED0B5D",
          "--TextField-brandBorderFocusedColor": "#ED0B5D",
          "& > fieldset > legend": {
            fontSize: `calc(0.75 * ${labelFontSize})`,
          },
          "& label.Mui-focused": {
            color: "#ED0B5D",
          },
        },
      },
    },
    MuiInputLabel: {
      defaultProps: {
        style: {
          fontSize: "20px",
          color: "#ED0B5D",
        },
      },

      styleOverrides: {
        root: {
          "--TextField-brandBorderColor": "#41273B",
          "--TextField-brandBorderHoverColor": "#ED0B5D",
          "--TextField-brandBorderFocusedColor": "#ED0B5D",
          "& label.Mui-focused": {
            color: "var(--TextField-brandBorderFocusedColor)",
          },
          fontSize: labelFontSize,
        },
      },
    },

    MuiListItemIcon: {
      styleOverrides: {
        root: {
          minWidth: 43,
        },
      },
    },

    MuiFormControl: {
      styleOverrides: {
        root: {
          "& > fieldset > legend": {
            fontSize: `calc(0.75 * ${labelFontSize})`,
          },
        },
      },
    },

    MuiTextField: {
      defaultProps: {
        inputProps: {
          style: {
            fontSize: "20px",
            color: "#41273B", // This sets the text field font color
          },
        },
        InputLabelProps: {
          style: {
            fontSize: labelFontSize,
            color: "#ED0B5D",
          },
        },
      },
      styleOverrides: {
        root: {
          "--TextField-brandBorderColor": "#41273B",
          "--TextField-brandBorderHoverColor": "#ED0B5D",
          "--TextField-brandBorderFocusedColor": "#ED0B5D",
          "& label.Mui-focused": {
            color: "var(--TextField-brandBorderFocusedColor)",
          },
        },
      },
    },

    MuiOutlinedInput: {
      defaultProps: {
        style: {
          fontSize: "20px",
        },
      },
      styleOverrides: {
        notchedOutline: {
          borderColor: "var(--TextField-brandBorderColor)",
        },
        root: {
          [`&:hover .${outlinedInputClasses.notchedOutline}`]: {
            borderColor: "var(--TextField-brandBorderHoverColor)",
          },
          [`&.Mui-focused .${outlinedInputClasses.notchedOutline}`]: {
            borderColor: "var(--TextField-brandBorderFocusedColor)",
          },
          "& > fieldset > legend": {
            fontSize: `calc(0.75 * ${labelFontSize})`,
          },
        },
      },
    },
    MuiFilledInput: {
      styleOverrides: {
        root: {
          "&::before, &::after": {
            borderBottom: "2px solid var(--TextField-brandBorderColor)",
          },
          "&:hover:not(.Mui-disabled, .Mui-error):before": {
            borderBottom: "2px solid var(--TextField-brandBorderHoverColor)",
          },
          "&.Mui-focused:after": {
            borderBottom: "2px solid var(--TextField-brandBorderFocusedColor)",
          },
        },
      },
    },

    MuiInput: {
      styleOverrides: {
        root: {
          "&::before": {
            borderBottom: "2px solid var(--TextField-brandBorderColor)",
          },
          "&:hover:not(.Mui-disabled, .Mui-error):before": {
            borderBottom: "2px solid var(--TextField-brandBorderHoverColor)",
          },
          "&.Mui-focused:after": {
            borderBottom: "2px solid var(--TextField-brandBorderFocusedColor)",
          },
        },
      },
    },
  },
  typography: {
    body2: {
      fontSize: "13px",
    },
    helptext: {
      fontSize: "16px",
      color: "#41273B",
    },
    menulink: {
      color: "#FFFFFF",
    },
    body1: {
      color: "#41273B",
    },
    h1: {
      color: "#ED0B5D",
      fontSize: "22px"
    },
    h2: {
      color: "#ED0B5D",
      fontSize: "22px"
    },
    h3: {
      color: "#41273B",
      fontSize: "20px"
    },
    h4: {
      color: "#FFFFFF",
      fontSize: "21px"
    },
    h5: {
      fontSize: "16px",
      color: "#FFFFFF",
    },
    h6: {
      color: "#41273B",
    },
    error: {
      color: "#fc9a9a",
    },
  },
  palette: {
    background: {
      default: "#ffffff",
      paper: "#B70C4E",
    },
    primary: {
      main: "#B70C4E",
    },
    secondary: {
      main: "#ED0B5D",
    },
    info: {
      main: "#41273B",
    }
  },
});

root.render(
  <React.StrictMode>
    <GoogleReCaptchaProvider reCaptchaKey={RECAPTCHA_SITE_KEY}>
      <ThemeProvider theme={theme}>
        <Provider store={store}>
            <Helmet>
              <title>Voucher Protect | Software Reseller</title>
              <meta
                name="description"
                content="Redeem your license today"
              />
            </Helmet>
            <CssBaseline />
            <App />
            <GoogleReCaptcha            
              onVerify={(token : string) => {
                try {
                  console.log("On verify called");
                  store.dispatch(recaptchaOnChanged(token));
                } catch (error) {
                  console.log(error)
                }
                
              }} />
        </Provider>
      </ThemeProvider>
    </GoogleReCaptchaProvider>
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
